import { useEffect, useRef, useState, type PointerEvent as PointerReact } from "react";
import { useTourState, useTourStoreApi } from "@heroncendre/player";
import { usePiegeFocus } from "../guide/usePiegeFocus.js";
import { useReducedMotion } from "../useReducedMotion.js";
import { etatInfos, useEtatInfos } from "./etatInfos.js";
import { IconeAccueil, IconeChevronDroite, IconeCommandes, IconeCredits, IconePleinEcran } from "./icones.js";
import { fermerAuGlisser } from "./logiqueInfos.js";
import { empiler } from "./retourSysteme.js";
import { Signature } from "./Signature.js";
import { useClavierSurcouche } from "./useClavierSurcouche.js";
import { useInfos, usePleinEcran } from "./useInfos.js";
import * as s from "./infosStyles.js";
import "./infos.css";

/**
 * ============================================================================
 *  FeuilleInfos.tsx — Le menu d'informations, sur téléphone
 * ----------------------------------------------------------------------------
 *  Référence : `sources/design/design_handoff_credits/` (9b). Une feuille basse pleine
 *  largeur, sur un voile : la langue (FR / EN — le sélecteur du haut n'existe pas sur
 *  téléphone), les mêmes actions que le menu desktop en lignes de 56 px, la signature.
 *
 *  Trois façons de la fermer : un toucher sur le voile, un glisser vers le bas (par la
 *  poignée — le contenu garde son défilement), le bouton Retour d'Android
 *  (`retourSysteme`). Pendant que les crédits la recouvrent, elle cède le clavier et le
 *  piège de focus, mais reste montée : « ‹ Retour » la retrouve telle quelle.
 * ==========================================================================*/

export function FeuilleInfos({ surAccueil }: { surAccueil: () => void }) {
  const infos = useInfos();
  const { t, language } = infos;
  const store = useTourStoreApi();
  const langues = useTourState((e) => e.tour.languages);
  const credits = useEtatInfos((e) => e.session.credits);
  const reduit = useReducedMotion();
  const pleinEcran = usePleinEcran();
  const racine = useRef<HTMLDivElement>(null);
  const glisser = useGlisser(infos.fermerMenu);

  // Le bouton Retour ferme la feuille. L'appel le plus récent de `fermerMenu` est lu par la ref.
  const fermer = useRef(infos.fermerMenu);
  fermer.current = infos.fermerMenu;
  useEffect(() => empiler(() => fermer.current()), []);

  usePiegeFocus(
    racine,
    !credits,
    () => racine.current?.querySelector<HTMLElement>('[data-inf-ligne="credits"]'),
    () => etatInfos.lire().origine,
  );

  useClavierSurcouche(!credits, (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      infos.fermerMenu();
    }
  });

  return (
    <>
      <div aria-hidden className="inf-voile" onClick={infos.fermerMenu} style={s.voile(0.55)} />
      <div
        ref={racine}
        role="dialog"
        aria-modal="true"
        aria-label={t("infoBienvenue")}
        className="inf-feuille"
        style={{
          ...s.feuille,
          // L'animation d'entrée garde `transform` (fill-mode `both`) : elle céderait la main
          // au doigt seulement une fois retirée, et ne doit pas se rejouer au relâcher.
          ...(glisser.aGlisse ? { animation: "none" } : null),
          ...(glisser.decalage !== null ? { transform: `translateY(${glisser.decalage}px)` } : null),
          transition: glisser.decalage === null && !reduit ? "transform .25s ease" : "none",
        }}
      >
        <div aria-hidden {...glisser.poignee} style={s.zonePoignee}>
          <span style={s.poignee} />
        </div>

        <h2 style={s.titreFeuille}>{t("infoBienvenue")}</h2>

        {langues.length > 1 && (
          <div style={s.rangeeLangue}>
            <span id="inf-langue">{t("infoLangue")}</span>
            <div role="group" aria-labelledby="inf-langue" style={s.segmente}>
              {langues.map((code) => (
                <button
                  key={code}
                  type="button"
                  className="inf-langue"
                  aria-pressed={code === language}
                  onClick={() => store.getState().setLanguage(code)}
                  style={s.choixLangue(code === language)}
                >
                  {code.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        )}

        <button type="button" className="inf-ligne" data-inf-ligne="credits" onClick={infos.ouvrirCredits} style={s.ligne(56, 15)}>
          <IconeCredits />
          {t("infoCredits")}
          <span style={{ marginLeft: "auto", display: "inline-flex" }}>
            <IconeChevronDroite taille={16} />
          </span>
        </button>
        <button type="button" className="inf-ligne" onClick={infos.rejouerIndice} style={s.ligne(56, 15)}>
          <IconeCommandes />
          {t("infoCommandes")}
        </button>
        {pleinEcran.disponible && (
          <button
            type="button"
            className="inf-ligne"
            onClick={() => {
              pleinEcran.basculer();
              infos.fermerMenu();
            }}
            style={s.ligne(56, 15)}
          >
            <IconePleinEcran sortie={pleinEcran.plein} />
            {pleinEcran.plein ? t("infoQuitterPleinEcran") : t("infoPleinEcran")}
          </button>
        )}

        <button type="button" className="inf-ligne" onClick={() => infos.allerAccueil(surAccueil)} style={s.ligne(56, 15)}>
          <IconeAccueil />
          {t("infoAccueil")}
        </button>

        <Signature t={t} lien surLien={infos.lien} />
      </div>
    </>
  );
}

/**
 * Tirer la feuille vers le bas par sa poignée. Elle suit le doigt (jamais au-dessus de sa
 * place) ; au relâcher, `fermerAuGlisser` décide : se fermer, ou remonter.
 */
function useGlisser(fermer: () => void) {
  const [decalage, setDecalage] = useState<number | null>(null);
  const [aGlisse, setAGlisse] = useState(false);
  const depart = useRef<{ y: number; t: number; hauteur: number } | null>(null);

  const poignee = {
    onPointerDown: (event: PointerReact<HTMLDivElement>) => {
      event.currentTarget.setPointerCapture(event.pointerId);
      const feuille = event.currentTarget.parentElement;
      depart.current = { y: event.clientY, t: performance.now(), hauteur: feuille?.offsetHeight ?? 1 };
      setAGlisse(true);
      setDecalage(0);
    },
    onPointerMove: (event: PointerReact<HTMLDivElement>) => {
      if (depart.current) setDecalage(Math.max(0, event.clientY - depart.current.y));
    },
    onPointerUp: (event: PointerReact<HTMLDivElement>) => {
      const d = depart.current;
      depart.current = null;
      setDecalage(null);
      if (d && fermerAuGlisser(event.clientY - d.y, performance.now() - d.t, d.hauteur)) fermer();
    },
    onPointerCancel: () => {
      depart.current = null;
      setDecalage(null);
    },
  };

  return { decalage, aGlisse, poignee };
}
